//ユーザーCSVアップロードページ
import React, { Fragment, useMemo, useRef, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Select,
  Modal,
  Table,
  Radio,
  Spin,
  Alert,
  Space,
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import Axios from 'axios';
import config from '../../Config/key';
import Papa from 'papaparse';
import {
  decodeCsvBuffer,
  validateCsvHeaders,
  formatHeaderValidationError,
  extractAxiosErrorMessage,
} from '../../../utils/csvUploadHelper';
import {
  buildRosterRows,
  buildRosterErrorCsv,
  joinInputBelong,
  buildDepartmentPrefixes,
  completeDepartmentChain,
  normalizeRecord,
  validateRecordDepartments,
  triggerBlobDownload,
  ymd8,
  normalizeSexValue,
  buildSampleRosterCsv,
  SAMPLE_CSV_FILE_NAMES,
} from '../../../utils/orgRosterHelper';
import {
  buildCompanyRule,
  useCompanyRule,
  collectCompanyErrors,
  hasCompanyKey,
  mergeRowErrors,
  countErrorKinds,
  formatCompanyErrorDetail,
} from '../../../utils/companyRule';
import { StatusBadge } from '../../common';
// const bcrypt = require('bcrypt');
// const saltRounds = 10;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// CSV に必須の列（email のみ）
const REQUIRED_COLUMNS = ['email'];

// 組織検証エラー行テーブルのカラム（行番号 / 氏名 / 入力された所属 / 理由 / 候補）
const ORG_ERROR_COLUMNS = [
  { title: '行番号', dataIndex: 'row', key: 'row', width: 80 },
  { title: '氏名', dataIndex: 'name', key: 'name', width: 140 },
  { title: '入力された所属', dataIndex: 'belong', key: 'belong' },
  { title: 'エラー理由', dataIndex: 'error', key: 'error' },
  {
    title: '候補',
    dataIndex: 'suggestions',
    key: 'suggestions',
    render: (list) =>
      list && list.length > 0
        ? list.map((s) => s.path).filter((p) => p).join(' / ')
        : '—',
  },
];

// 数値スキーマ項目（role/manager/sex/age_range 等）の空文字を undefined にする（#5: '' → Number キャスト失敗の回避）
// undefined は insert 時は schema default / overwrite 時は既存値保持となる
const numOrUndef = (v) =>
  v === '' || v === null || v === undefined ? undefined : v;
// Boolean スキーマ項目（inactive 等）の空文字を undefined に（#5: '' → Boolean キャスト失敗の回避）
const boolOrUndef = (v) =>
  v === '' || v === null || v === undefined ? undefined : v;

// 既存ユーザー確認モーダルの表カラム（メール / 姓 / 名 / 所属）
const EXISTING_USER_COLUMNS = [
  { title: 'メールアドレス', dataIndex: 'email', key: 'email' },
  { title: '姓', dataIndex: 'lastname', key: 'lastname' },
  { title: '名', dataIndex: 'name', key: 'name' },
  {
    title: '所属',
    key: 'belong',
    render: (_, record) =>
      `${record.company || ''}${
        record.group_code ? `（${record.group_code}）` : ''
      }`,
  },
];

function CSVUserUploadPage(props) {
  const [fileData, setFileData] = useState();
  const [fileJSON, setFileJSON] = useState();
  const [groups, setGroups] = useState([]); //グループ名とグループコード

  let department = [];
  let position = [];
  let empType = [];
  let levelCode = [];
  let goOut = [];

  // const [projectDescriptionValue, setProjectDescriptionValue] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [submitButtonDisable, setSubmitButtonDisable] = useState(true);

  // 既存ユーザー（重複）確認モーダル用
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [existingUsers, setExistingUsers] = useState([]);
  const [processing, setProcessing] = useState(false);
  // 「CSVで上書き」時のパスワードの扱い（keep=変更しない / all=全員変更 / initial_only=初期パスワードのままの人だけ変更）
  const [passwordMode, setPasswordMode] = useState('keep');

  // アップロード結果モーダル用（完了後に件数を表示）
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultCounts, setResultCounts] = useState({
    inserted: 0,
    updated: 0,
    skipped: 0,
    excluded: 0,
  });

  // ─── 組織マスター検証（名簿連携） ───
  // orgVersion: undefined=未判定 / null=組織表未登録（従来方式）/ object=有効バージョンあり
  const [orgVersion, setOrgVersion] = useState(undefined);
  const [orgResults, setOrgResults] = useState(null); // validate-roster の results 配列
  const [orgChecking, setOrgChecking] = useState(false);

  // ─── 会社名検証（系列会社ありグループのみ） ───
  // companySubmitResults: 登録ボタン押下時（新規/既存の判定後）に確定した会社名エラー行。null=未確定
  const [companySubmitResults, setCompanySubmitResults] = useState(null);
  // 登録処理（buildPayload）が参照する確定済みの会社名エラー行（同一ティック内の state 反映待ちを避ける）
  const companyFinalErrorsRef = useRef(null);
  // 選択中グループの会社名ルール（系列会社ありグループのみ GET /api/group/:code/company-list を 1 回呼ぶ。
  // 取得完了まで・失敗時は取得済みのグループ一覧の系列会社マスタを使う）
  const selectedGroup = useMemo(
    () => groups.find((g) => g.group_code === groupCode),
    [groups, groupCode]
  );
  const companyRule = useCompanyRule(groupCode, selectedGroup);
  // 系列会社ありグループか（validate-roster に company を送るか）。グループ選択直後の検証でも使うため
  // 非同期の会社名ルールではなく、取得済みのグループ一覧の has_affiliates で判定する
  const isAffiliateGroup = (gCode) =>
    buildCompanyRule(groups.find((g) => g.group_code === gCode)).enabled;
  // ファイル読込時点で判定できる会社名エラー（company 列がある行は全行を権限を問わず検証。
  // 系列会社ありグループでは「所属フルパス」先頭の会社名で補完した行・会社名列との食い違いも対象）
  // ※ company 列が無い行は新規/既存が分かる登録時（onSubmit）に検証する
  const companyPreResults = useMemo(() => {
    if (!companyRule.enabled || !Array.isArray(fileJSON)) return [];
    return collectCompanyErrors(companyRule, fileJSON, { isTarget: hasCompanyKey });
  }, [companyRule, fileJSON]);
  const companyErrors = companyRule.enabled
    ? companySubmitResults || companyPreResults
    : [];

  // 会社名の確定結果を破棄する（ファイル・企業の変更時）
  const resetCompanyResults = () => {
    setCompanySubmitResults(null);
    companyFinalErrorsRef.current = null;
  };

  const onDrop = async (files) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onabort = () => console.log('file reading was aborted');
    reader.onerror = () => console.log('file reading failed');
    reader.onload = (e) => {
      //ファイルの読み込みが終わったら発火
      // decodeCsvBuffer で文字コード自動判定 + 先頭 BOM 除去まで一括処理
      const unicodeString = decodeCsvBuffer(e.target.result);
      Papa.parse(unicodeString, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: async (results) => {
          // 必須列の存在検証（BOM は decodeCsvBuffer で除去済み、その他不可視文字を捕捉）
          const headers = (results.meta && results.meta.fields) || [];
          const v = validateCsvHeaders(headers, REQUIRED_COLUMNS);
          if (!v.ok) {
            message.error({
              content: formatHeaderValidationError(v),
              key: 'csvupload',
              duration: 10,
            });
            setFileJSON(undefined);
            setFileData(
              'エラー：CSVヘッダーを確認の上、再アップロードしてください。'
            );
            return;
          }
          setFileData(unicodeString);
          setFileJSON(results.data);
          resetCompanyResults();
          // 企業が既に選択済みなら、この CSV を組織マスターで検証する
          if (groupCode) {
            const version = await loadOrgTree(groupCode);
            if (version) runRosterValidation(groupCode, results.data);
            else setOrgResults(null);
          } else {
            setOrgResults(null);
          }
          //const courseInfo = courseMerge(results.data);
          // setProjectKeyValue(`${courseInfo.course_id}_${ymdhms()}`);
          // console.log({ courseInfo });
        },
      });
    };
    reader.readAsArrayBuffer(file); //ファイルを読み込む
    const group = await Axios.get('/api/group/');
    setGroups(group.data.groups);
    // console.log(group.data.groups);
  };

  // ─── 組織マスター（有効バージョン + ユニット）を取得 ───
  // version が無ければ null（＝組織表未登録・従来方式）を返す。
  const loadOrgTree = async (gCode) => {
    try {
      const res = await Axios.get(`/api/org/tree/${gCode}`);
      if (res.data && res.data.success) {
        const version = res.data.version || null;
        setOrgVersion(version);
        return version;
      }
    } catch (error) {
      // 取得失敗時は従来方式にフォールバック（登録自体はブロックしない）
      console.log({ error });
    }
    setOrgVersion(null);
    return null;
  };

  // ─── CSV 各行の所属を組織マスターと突き合わせ（validate-roster） ───
  const runRosterValidation = async (gCode, rows) => {
    setOrgChecking(true);
    setOrgResults(null);
    try {
      // 系列会社ありグループのみ各行に company を含める（それ以外は従来どおり）
      const res = await Axios.post(`/api/org/validate-roster/${gCode}`, {
        rows: buildRosterRows(rows, { affiliate: isAffiliateGroup(gCode) }),
      });
      if (res.data && res.data.success) {
        setOrgResults(res.data.results || []);
      } else {
        throw new Error('組織検証に失敗しました。');
      }
    } catch (error) {
      console.log({ error });
      message.error({
        content: extractAxiosErrorMessage(error, '組織検証に失敗しました。'),
        key: 'orgcheck',
        duration: 5,
      });
      setOrgResults(null);
    } finally {
      setOrgChecking(false);
    }
  };

  // ─── 組織検証エラー行を CSV（BOM UTF-8）でダウンロード ───
  const downloadOrgErrorCsv = () => {
    // 系列会社ありグループでは会社名エラーも合流させた一覧を出力する
    const errors = companyRule.enabled
      ? displayErrorRows
      : (orgResults || []).filter((r) => !r.ok);
    if (errors.length === 0) return;
    const csv = buildRosterErrorCsv(errors, fileJSON);
    triggerBlobDownload(
      csv,
      `組織検証エラー_${groupCode}_${ymd8()}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  //yyyymmddhhmmss
  const ymdhms = () => {
    var date = new Date();
    return (
      date.getFullYear() +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      ('0' + date.getDate()).slice(-2) +
      ('0' + date.getHours()).slice(-2) +
      ('0' + date.getMinutes()).slice(-2) +
      ('0' + date.getSeconds()).slice(-2)
      // date.getMilliseconds()
    );
  };

  const ymd = (tdate) => {
    let date = new Date(tdate);

    // console.log({ tdate });
    return (
      date.getFullYear() +
      '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + date.getDate()).slice(-2) +
      'T' +
      ('0' + date.getHours()).slice(-2) +
      ':' +
      ('0' + date.getMinutes()).slice(-2) +
      ':' +
      ('0' + date.getSeconds()).slice(-2) +
      '.000Z'
      // date.getMilliseconds()
    );
  };

  // CSV の各行から登録用 userData 配列とグループ集計データ（GSD）を生成する
  // ※ 既存の登録ロジックを踏襲。group の最新マスター配列を取得し、CSV 内の部署・職位等を追加する
  const buildPayload = async () => {
    // 再試行時の累積を避けるため毎回グループから取り直す
    department = [];
    position = [];
    empType = [];
    levelCode = [];
    goOut = [];
    const today = new Date(Date.now());

    const res = await Axios.get(`/api/group/${groupCode}`);
    if (!res.data.success) {
      throw new Error('企業情報の取得ができませんでした。');
    }
    if (res.data.group.department) department = res.data.group.department;
    if (res.data.group.position) position = res.data.group.position;
    if (res.data.group.emp_type) empType = res.data.group.emp_type;
    if (res.data.group.level_code) levelCode = res.data.group.level_code;
    if (res.data.group.go_out) goOut = res.data.group.go_out;

    // 組織マスターがある場合のみ、エラー行集合を作る
    // ※ C′ 設計（2026-07）で org_code の dual-write を廃止したため、
    //   ここでは検証エラー行の除外にのみ validate-roster の結果を使う（org_code は付与しない）
    const hasOrgMaster = !!orgVersion;
    const orgErrorRowSet = new Set();
    if (hasOrgMaster && orgResults) {
      orgResults.forEach((r) => {
        if (!r.ok) orgErrorRowSet.add(r.row);
      });
    }
    // 系列会社ありグループのみ: 会社名エラー行も除外対象に加える（登録時に確定した結果を使う）
    if (companyRule.enabled) {
      (companyFinalErrorsRef.current || []).forEach((r) => orgErrorRowSet.add(r.row));
    }
    let excludedCount = 0;

    const userData = [];
    fileJSON.forEach((rawRecord, idx) => {
      const rowNo = idx + 1; // 1 起点（validate-roster の row と対応）
      // 組織エラー行（系列会社ありグループでは会社名エラー行も）は登録対象から除外（正常行のみ登録）
      if (orgErrorRowSet.has(rowNo)) {
        excludedCount += 1;
        return;
      }
      // 「所属フルパス」列があれば department〜department7 に展開（個別列形式は不変）し、
      // 所属情報がある行は未記載の階層を '' で補完（所属 7 階層を 1 単位として扱い、上書き時に旧下位階層が残留しない）
      // 系列会社ありグループでは「所属フルパス」が「会社名／…」形式のため先頭を会社名として分解する
      const record = completeDepartmentChain(
        normalizeRecord(rawRecord, { affiliate: companyRule.enabled })
      );
      // 所属名の入力規則（「／」禁止・制御文字禁止・50 文字以内）に反する行があれば中断
      const deptNameError = validateRecordDepartments(record);
      if (deptNameError) {
        throw new Error(`${rowNo} 行目: ${deptNameError}`);
      }
      // 所属の経路プレフィックス（本社 / 本社／営業部 / …）を企業マスター（GSD）へ登録（7 階層まで）
      buildDepartmentPrefixes(record).forEach((prefix) => {
        if (!department.find((item) => item === prefix)) department.push(prefix);
      });
      let dpString = '';
      if (record.position) {
        dpString = record.position;
        if (!position.find((item) => item === dpString)) {
          position.push(dpString);
        }
      }
      if (record.emp_type) {
        dpString = record.emp_type;
        if (!empType.find((item) => item === dpString)) {
          empType.push(dpString);
        }
      }
      if (record.level_code) {
        dpString = record.level_code;
        if (!levelCode.find((item) => item === dpString)) {
          levelCode.push(dpString);
        }
      }
      if (record.go_out) {
        dpString = record.go_out;
        if (!goOut.find((item) => item === dpString)) {
          goOut.push(dpString);
        }
      }
      let change_password = false;
      if (record.change_password) change_password = record.change_password;
      // email は前後空白を除去して保持（#3: フロント照合と DB 格納・更新条件の不一致を防ぐ）
      const cleanEmail =
        typeof record.email === 'string' ? record.email.trim() : record.email;
      userData.push({
        lastname: record.lastname,
        lastname_yomi: record.lastname_yomi,
        name: record.name,
        name_yomi: record.name_yomi,
        email: cleanEmail,
        password: record.password,
        company: record.company,
        department: record.department,
        emp_no: record.emp_no,
        role: numOrUndef(record.role),
        group_code: groupCode,
        department2: record.department2,
        department3: record.department3,
        department4: record.department4,
        department5: record.department5,
        department6: record.department6,
        department7: record.department7,
        position: record.position,
        manager: numOrUndef(record.manager),
        level_code: record.level_code,
        go_out: record.go_out,
        // 性別は「男／女」でも受け付け 1／2 へ変換（名簿テンプレート・Excel アップロードと同じ規則）
        sex: numOrUndef(normalizeSexValue(record.sex)),
        emp_type: record.emp_type,
        birthday: record.birthday,
        join_company: record.join_company,
        change_password: change_password,
        age_range: numOrUndef(record.age_range),
        work_location: record.work_location,
        work_time_rule: record.work_time_rule,
        work_time: record.work_time,
        join_type: record.join_type,
        init_pass: record.password,
        inactive: boolOrUndef(record.inactive),
        change_history: [
          {
            input_date: today,
            department: record.department,
            department2: record.department2,
            department3: record.department3,
            department4: record.department4,
            department5: record.department5,
            department6: record.department6,
            department7: record.department7,
            position: record.position,
            level_code: record.level_code,
            emp_type: record.emp_type,
            go_out: record.go_out,
            inactive: boolOrUndef(record.inactive),
            // 系列会社ありグループのみ会社名を履歴に記録（それ以外はキー自体を付けない）
            ...(companyRule.enabled ? { company: record.company } : {}),
          },
        ],
      });
    });

    const groupData = { department, position, empType, levelCode, goOut };
    return { userData, groupData, excludedCount };
  };

  // モード別の一括実行（overwrite=上書き / skip=既存はスキップ）
  // ※ checkemails で既存が 0 件の場合も skip で呼ぶ（既存が無いので全件 insert される）
  // ※ pwMode は overwrite 時のみ有効（keep/all/initial_only）
  // ※ グループ更新（GSD）も csvuserupsert に渡して同一トランザクションで処理（#7）
  const executeUpload = async (mode, pwMode = 'keep') => {
    setProcessing(true);
    try {
      message.loading({
        content: 'CSVアップロード中...（件数が多い場合は時間がかかります）',
        key: 'csvupload',
        duration: 0,
      });

      const { userData, groupData, excludedCount } = await buildPayload();

      // 登録対象が 0 件（全行が組織エラー）の場合は中断
      if (!userData || userData.length === 0) {
        message.error({
          content:
            '登録できる正常な行がありません。組織検証エラーを解消してください。',
          key: 'csvupload',
          duration: 6,
        });
        throw new Error('登録対象が 0 件です。');
      }

      const res3 = await Axios.post('/api/users/csvuserupsert', {
        userData,
        mode,
        passwordMode: pwMode,
        groupCode,
        groupData,
      });

      if (res3.data.success) {
        const { inserted = 0, updated = 0, skipped = 0 } = res3.data;
        const excludedText =
          excludedCount > 0 ? ` / ${excludedLabel}：${excludedCount}名` : '';
        setFileData(
          `CSVアップロードが完了しました。\n新規登録：${inserted}名 / 更新：${updated}名 / スキップ：${skipped}名${excludedText}`
        );
        message.destroy('csvupload');
        // 完了後は結果モーダルで件数を表示し、OK で「/」へ遷移する
        setResultCounts({ inserted, updated, skipped, excluded: excludedCount });
        setResultModalOpen(true);
      } else {
        message.error({
          content: 'CSVアップロードに失敗しました。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('CSVアップロードに失敗しました。');
      }
    } catch (error) {
      console.log({ error });
      // バックエンドの具体的なエラー本文を抽出して表示（運用担当が pm2 logs 参照不要）
      // サーバーが会社名の検証エラー（company_errors）を返した場合は先頭 5 件を併記する
      const companyDetail = formatCompanyErrorDetail(error);
      message.error({
        content:
          extractAxiosErrorMessage(error, 'CSVアップロードに失敗しました。') +
          companyDetail,
        key: 'csvupload',
        duration: companyDetail ? 10 : 5,
      });
    } finally {
      setProcessing(false);
    }
  };

  // 「アップロード」ボタン: 事前チェック（CSV内部重複 → 既存メール照合）
  const onSubmit = async (event) => {
    event.preventDefault();
    try {
      // CSV が正常に読み込まれているかを確認（必須列検証で弾かれた場合は fileJSON が undefined）
      if (!fileJSON || !Array.isArray(fileJSON) || fileJSON.length === 0) {
        message.error({
          content:
            '有効なCSVファイルがアップロードされていません。CSVを再確認してください。',
          key: 'csvupload',
          duration: 3,
        });
        throw new Error('CSVデータがありません。');
      }
      if (!groupCode) {
        message.error({
          content: '企業を選択してください。',
          key: 'csvupload',
          duration: 2,
        });
        throw new Error('企業コードがありません。');
      }

      // ① CSV 内部のメール重複・空メールをチェック → あれば中断
      // email は trim + 小文字化で照合（#8: 大文字小文字違いだけの重複も検出）
      const emails = fileJSON.map((r) =>
        typeof r.email === 'string' ? r.email.trim() : ''
      );
      const emptyCount = emails.filter((e) => !e).length;
      const seen = new Set();
      const dupSet = new Set();
      emails.forEach((e) => {
        if (!e) return;
        const key = e.toLowerCase();
        if (seen.has(key)) dupSet.add(e);
        seen.add(key);
      });
      if (emptyCount > 0) {
        message.error({
          content: `メールアドレスが空の行が ${emptyCount} 件あります。CSVを修正してください。`,
          key: 'csvupload',
          duration: 6,
        });
        return;
      }
      if (dupSet.size > 0) {
        const dupList = Array.from(dupSet).join('、');
        message.error({
          content: `CSV内にメールアドレスの重複があります（${dupSet.size}件）：${dupList}。重複を解消してから再アップロードしてください。`,
          key: 'csvupload',
          duration: 10,
        });
        return;
      }

      // ② DB の既存ユーザー（全域）を照合
      message.loading({
        content: '登録済みメールを確認中...',
        key: 'csvupload',
        duration: 0,
      });
      const check = await Axios.post('/api/users/checkemails', { emails });
      const existing = (check.data && check.data.existing) || [];

      // ③ 新規登録ユーザーはパスワード必須（#4: 空だと insert がクラッシュ/空ハッシュになる）
      // 既存（上書き/スキップ対象）はパスワード空でも可。大文字小文字無視で既存判定
      const existingLowerSet = new Set(
        existing.map((u) => String(u.email).trim().toLowerCase())
      );
      const newMissingPw = fileJSON
        .filter(
          (r) =>
            !existingLowerSet.has(String(r.email || '').trim().toLowerCase())
        )
        .filter((r) => !r.password || String(r.password).trim() === '')
        .map((r) => (r.email || '').trim());
      if (newMissingPw.length > 0) {
        message.error({
          content: `新規登録ユーザーのパスワードが空です（${newMissingPw.length}件）：${newMissingPw.join(
            '、'
          )}。パスワードを入力してください。`,
          key: 'csvupload',
          duration: 10,
        });
        return;
      }

      // ④ 系列会社ありグループのみ: 会社名を検証（新規/既存が確定したこの時点で最終判定）
      //   company 列がある行（所属フルパスの会社名で補完した行を含む）= 権限を問わず検証（空も対象）
      //   company 列が無い行 = 新規ユーザー行のみ検証（既存ユーザーは上書きでも会社名を送らないため対象外）
      if (companyRule.enabled) {
        const finalCompanyErrors = collectCompanyErrors(companyRule, fileJSON, {
          isTarget: (r) =>
            hasCompanyKey(r) ||
            !existingLowerSet.has(String(r.email || '').trim().toLowerCase()),
        });
        const shownRows = new Set(companyErrors.map((r) => r.row));
        const hasUnshown = finalCompanyErrors.some((r) => !shownRows.has(r.row));
        setCompanySubmitResults(finalCompanyErrors);
        companyFinalErrorsRef.current = finalCompanyErrors;
        if (hasUnshown) {
          // 画面にまだ表示していない会社名エラーがある場合は、内容を確認してもらうため一旦止める
          message.warning({
            content: `会社名の検証エラーが ${finalCompanyErrors.length} 件あります。エラー内容を確認のうえ、もう一度「エラー行を除いて登録」を押してください。`,
            key: 'csvupload',
            duration: 8,
          });
          return;
        }
      }

      if (existing.length === 0) {
        // 重複なし → そのまま全件登録（skip モードでも結果は全件 insert）
        message.destroy('csvupload');
        await executeUpload('skip');
      } else {
        // 重複あり → モーダルで 3 択を提示
        message.destroy('csvupload');
        setExistingUsers(existing);
        setDuplicateModalOpen(true);
      }
    } catch (error) {
      console.log({ error });
      message.error({
        content: extractAxiosErrorMessage(error, 'メール確認に失敗しました。'),
        key: 'csvupload',
        duration: 5,
      });
    }
  };

  // モーダルの 3 択ハンドラー
  const handleOverwrite = () => {
    setDuplicateModalOpen(false);
    executeUpload('overwrite', passwordMode);
  };
  const handleSkip = () => {
    setDuplicateModalOpen(false);
    executeUpload('skip');
  };
  const handleAbort = () => {
    // 何もしない（中断）
    setDuplicateModalOpen(false);
    message.info({
      content: '処理を中断しました。',
      key: 'csvupload',
      duration: 2,
    });
  };

  // 結果モーダルを閉じてホームへ遷移
  const handleResultClose = () => {
    setResultModalOpen(false);
    props.history.push('/');
  };

  const onChangeGroup = async (value) => {
    if (!value) return;
    setGroupCode(value);
    setSubmitButtonDisable(false);
    resetCompanyResults();
    // 企業選択時に組織マスターの有無を判定し、CSV があれば検証する
    setOrgResults(null);
    const version = await loadOrgTree(value);
    if (version && fileJSON && fileJSON.length > 0) {
      runRosterValidation(value, fileJSON);
    }
  };

  // ─── 組織検証の派生値（レンダー用） ───
  const hasOrgMaster = !!orgVersion; // 組織表あり
  const orgErrorRows = (orgResults || []).filter((r) => !r.ok);
  const orgOkCount = (orgResults || []).filter((r) => r.ok).length;
  const orgHasErrors = hasOrgMaster && orgResults && orgErrorRows.length > 0;
  // エラー行テーブル用の行データ
  const orgErrorTableData = orgErrorRows.map((r) => {
    const rec = (fileJSON && fileJSON[r.row - 1]) || {};
    return {
      key: r.row,
      row: r.row,
      name: `${rec.lastname || ''} ${rec.name || ''}`.trim() || '—',
      belong: joinInputBelong(rec) || '—',
      error: r.error || '—',
      suggestions: r.suggestions || [],
    };
  });
  // ─── 会社名検証の合流（系列会社ありグループのみ。それ以外は組織検証の値をそのまま使う） ───
  // サンプル CSV（系列会社ありグループは会社名付き・「会社名／…」形式の例）
  const downloadSampleCsv = () => {
    triggerBlobDownload(
      buildSampleRosterCsv('create', { affiliate: companyRule.enabled }),
      SAMPLE_CSV_FILE_NAMES.create,
      'text/csv;charset=utf-8'
    );
  };
  const companyEnabled = companyRule.enabled;
  const displayErrorRows = companyEnabled
    ? mergeRowErrors(hasOrgMaster && orgResults ? orgErrorRows : [], companyErrors)
    : orgErrorRows;
  const showValidation = companyEnabled
    ? (hasOrgMaster && !!orgResults) || Array.isArray(fileJSON)
    : !!orgResults;
  // 系列会社ありグループの正常件数。組織表がある場合は「組織検証 OK かつ会社名エラー無し」の行だけを数える
  // （所属が未入力で組織検証の対象外になった行は、従来どおり正常件数に含めない＝登録ボタンの活性条件と揃える）
  const companyErrorRowSet = new Set((companyErrors || []).map((r) => r.row));
  const okCount = companyEnabled
    ? hasOrgMaster && orgResults
      ? orgResults.filter((r) => r.ok && !companyErrorRowSet.has(r.row)).length
      : Math.max(0, (Array.isArray(fileJSON) ? fileJSON.length : 0) - displayErrorRows.length)
    : orgOkCount;
  const hasRowErrors = companyEnabled ? displayErrorRows.length > 0 : orgHasErrors;
  const errorTableData = companyEnabled
    ? displayErrorRows.map((r) => {
        const rec = (fileJSON && fileJSON[r.row - 1]) || {};
        return {
          key: r.row,
          row: r.row,
          name: `${rec.lastname || ''} ${rec.name || ''}`.trim() || '—',
          belong: joinInputBelong(rec) || '—',
          error: r.error || '—',
          suggestions: r.suggestions || [],
        };
      })
    : orgErrorTableData;
  // エラー見出し（会社名エラーを含むときだけ文言を切り替える）
  // 系列会社ありグループでは validate-roster の field:'company' も会社名エラーとして数える
  const errorKinds = countErrorKinds(
    hasOrgMaster && orgResults ? orgErrorRows : [],
    companyErrors
  );
  const companyErrorCount = companyEnabled ? errorKinds.company : 0;
  const orgErrorCountShown = companyEnabled
    ? errorKinds.department
    : hasOrgMaster && orgResults
    ? orgErrorRows.length
    : 0;
  const errorHeading =
    companyErrorCount === 0
      ? '所属が組織表と一致しない行があります。'
      : orgErrorCountShown === 0
      ? '会社名が系列会社マスタと一致しない行があります。'
      : '所属が組織表と一致しない行、または会社名に誤りがある行があります。';
  // 完了表示の除外ラベル
  const excludedLabel = companyEnabled ? '除外（検証エラー）' : '除外（組織エラー）';
  // 送信ボタン: 登録可能な正常行が 0 件なら無効化
  const noValidRows =
    (hasOrgMaster && orgResults && orgOkCount === 0) ||
    (companyEnabled &&
      Array.isArray(fileJSON) &&
      fileJSON.length > 0 &&
      okCount === 0);

  return (
    <Fragment>
      <Spin
        spinning={processing}
        fullscreen
        tip="処理中です。完了までこのままお待ちください…"
      />
      <div style={{ maxWidth: '700px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>受講者 CSV アップロード</Title>
        </div>
        <Form onSubmit={onSubmit}>
          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❶</Title>
          {/* サンプル CSV（ヘッダー＝英字フィールド名・2026-10-07） */}
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Button icon={<DownloadOutlined />} onClick={downloadSampleCsv}>
              サンプル CSV ダウンロード
            </Button>
            <Text type="secondary" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
              ヘッダーは英字のフィールド名です。所属は「department_path」1 列（「／」区切り{companyRule.enabled ? '・先頭は系列会社名' : ''}）、または従来の department〜department7 の個別列で指定できます。
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* Drop zone */}
            <Dropzone onDrop={onDrop} multiple={false} maxSize={1000000000}>
              {({ getRootProps, getInputProps }) => (
                <div
                  style={{
                    width: '300px',
                    height: '240px',
                    border: '1px solid lightgray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <PlusOutlined style={{ fontSize: '3rem' }} />
                </div>
              )}
            </Dropzone>
            <div
              style={{
                width: '300px',
                height: '240px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text mark>
                CSVファイルをDrag &
                Dropするか、+を押してファイルを選択してください。
              </Text>
            </div>
          </div>
          <br />
          <div>
            <Title style={{ color: 'var(--brand-primary)' }}>❷</Title>
            {/* <Text strong>企業コードを入力してください（必須）</Text>
            <Input
              placeholder="一意になる企業コードを入力してください。"
              allowClear
              onChange={onChangeGroupCode}
              value={groupCode}
            /> */}
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-4)' }}>企業を選択してください。（必須）</Text>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Select
                showSearch
                style={{ marginLeft: 0, marginRight: 10, width: 300 }}
                placeholder="企業選択"
                optionFilterProp="children"
                onChange={onChangeGroup}
                value={groupCode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {groups.map((element) => {
                  // console.log({ element });
                  return (
                    <Option value={element.group_code} key={element._id}>
                      {element.group_name}
                    </Option>
                  );
                })}
              </Select>
            </div>
            <br />
          </div>

          {/* ─── 組織マスター検証（名簿連携） ─── */}
          {groupCode && orgVersion === null && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 'var(--space-4)' }}
              message="この企業は組織表が未登録のため、従来方式で登録されます。"
            />
          )}
          {groupCode && (hasOrgMaster || companyEnabled) && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Spin spinning={orgChecking}>
                {showValidation && (
                  <div>
                    <Space wrap style={{ marginBottom: 'var(--space-3)' }}>
                      <StatusBadge tone="success">
                        正常 {okCount} 件
                      </StatusBadge>
                      <StatusBadge tone={hasRowErrors ? 'error' : 'neutral'}>
                        エラー {displayErrorRows.length} 件
                      </StatusBadge>
                    </Space>

                    {hasRowErrors && (
                      <div>
                        <Space
                          wrap
                          style={{ marginBottom: 'var(--space-2)' }}
                        >
                          <Text strong style={{ color: 'var(--red-active)' }}>
                            {errorHeading}
                          </Text>
                          <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={downloadOrgErrorCsv}
                          >
                            エラーリストをCSVでダウンロード
                          </Button>
                        </Space>
                        <Table
                          size="small"
                          rowKey="key"
                          columns={ORG_ERROR_COLUMNS}
                          dataSource={errorTableData}
                          pagination={
                            errorTableData.length > 10
                              ? { pageSize: 10 }
                              : false
                          }
                          scroll={{ y: 280 }}
                        />
                        <Text
                          type="secondary"
                          style={{
                            display: 'block',
                            marginTop: 'var(--space-2)',
                          }}
                        >
                          ※ エラー行を除いた正常行のみ登録されます。
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Spin>
            </div>
          )}

          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❸</Title>
          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            disabled={submitButtonDisable || processing || noValidRows}
            loading={processing}
          >
            {hasRowErrors ? 'エラー行を除いて登録' : 'アップロード'}
          </Button>
          {noValidRows && (
            <Text
              type="secondary"
              style={{ display: 'block', marginTop: 'var(--space-2)' }}
            >
              {companyEnabled
                ? '登録できる正常な行がありません。検証エラーを解消してください。'
                : '登録できる正常な行がありません。組織検証エラーを解消してください。'}
            </Text>
          )}
        </Form>
        <TextArea rows={20} value={fileData} />
      </div>

      {/* 既存ユーザー（重複）確認モーダル */}
      <Modal
        title="既に登録済みのメールアドレスがあります"
        open={duplicateModalOpen}
        onCancel={handleAbort}
        maskClosable={false}
        width={720}
        footer={[
          <Button key="abort" onClick={handleAbort}>
            中断
          </Button>,
          <Button key="skip" onClick={handleSkip}>
            既存をスキップして登録
          </Button>,
          <Button key="overwrite" type="primary" onClick={handleOverwrite}>
            CSVで上書き
          </Button>,
        ]}
      >
        <p>
          下記の {existingUsers.length} 件は既にシステムに登録されています。
          処理方法を選択してください。
        </p>
        <ul style={{ marginBottom: 'var(--space-4)' }}>
          <li>
            <Text strong>CSVで上書き</Text>：
            既存ユーザーのプロフィールをCSVの内容で更新します（回答・設問トークンは保持）。
          </li>
          <li>
            <Text strong>既存をスキップして登録</Text>：
            既存ユーザーは変更せず、未登録のメールのみ新規登録します。
          </li>
          <li>
            <Text strong>中断</Text>：何も実行しません。
          </li>
        </ul>

        {/* 「CSVで上書き」時のパスワードの扱い（overwrite を選んだ時のみ有効） */}
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: 'var(--space-3)',
            border: '1px solid var(--grey-30)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <Text
            strong
            style={{ display: 'block', marginBottom: 'var(--space-2)' }}
          >
            「CSVで上書き」時のパスワードの扱い
          </Text>
          <Radio.Group
            value={passwordMode}
            onChange={(e) => setPasswordMode(e.target.value)}
          >
            <Radio
              value="keep"
              style={{ display: 'block', marginBottom: 'var(--space-1)' }}
            >
              パスワードはそのまま（変更しない）
            </Radio>
            <Radio
              value="all"
              style={{ display: 'block', marginBottom: 'var(--space-1)' }}
            >
              パスワードを全員変更する（CSVに値がある場合のみ）
            </Radio>
            <Radio value="initial_only" style={{ display: 'block' }}>
              初期パスワードのままの人だけ変更する（自分で変更済みの人は維持。CSVに値がある場合のみ）
            </Radio>
          </Radio.Group>
        </div>

        <Table
          size="small"
          rowKey="email"
          columns={EXISTING_USER_COLUMNS}
          dataSource={existingUsers}
          pagination={
            existingUsers.length > 10 ? { pageSize: 10 } : false
          }
          scroll={{ y: 280 }}
        />
      </Modal>

      {/* アップロード結果モーダル（完了後の件数表示） */}
      <Modal
        title="アップロードが完了しました"
        open={resultModalOpen}
        onCancel={handleResultClose}
        maskClosable={false}
        footer={[
          <Button key="ok" type="primary" onClick={handleResultClose}>
            OK
          </Button>,
        ]}
      >
        <p>CSVアップロードが正常に完了しました。</p>
        <ul style={{ lineHeight: 'var(--leading-loose)' }}>
          <li>
            新規登録：<Text strong>{resultCounts.inserted}</Text> 名
          </li>
          <li>
            更新：<Text strong>{resultCounts.updated}</Text> 名
          </li>
          <li>
            スキップ：<Text strong>{resultCounts.skipped}</Text> 名
          </li>
          {resultCounts.excluded > 0 && (
            <li>
              {excludedLabel}：
              <Text strong>{resultCounts.excluded}</Text> 名
            </li>
          )}
        </ul>
      </Modal>
    </Fragment>
  );
}

export default CSVUserUploadPage;
